// "The pond of water lilies": a dusk walk under tall trees → part the leaves → a hidden sunset pond → lilies open → her painting flies in against the dark.
const LILIES_FLOOR = [[-0.9, -2.2], [0.6, -2.8], [1.6, -1.6], [-1.9, -1.2], [0.1, -1.6], [2.3, -3.0], [-2.6, -2.8], [2.6, 0.8], [-2.4, 1.4]];
const LILIES_WALL = [[-0.35, 0.9], [0.25, 0.72], [0.7, 1.02], [-0.8, 0.62], [1.2, 0.85], [-1.3, 0.98]];

export const pondLilies = {
  slug: "the-lotus-pond",
  duration: 28,
  stages: [
    { id: "walk", at: 0, hint: "walking home at dusk" },
    { id: "glimpse", at: 5, hint: "something between the trunks" },
    { id: "stop", at: 7.4, hint: "" },
    { id: "leaves", at: 8.5, hint: "step closer · part the branches" },
    { id: "pond", at: 15, hint: "observing" },
    { id: "lilies", at: 17.5, hint: "the lilies open" },
  ],
  line: { text: "When you are open to finding beauty, the universe surprises you.", from: 19, to: 27.5, pos: "low" },
  touch: { kind: "part", from: 8.5, until: 16 },
  // painted in her pond painting's palette: the dusk walk home under tall trees (sky and path, far trunks, near trunks, streaming light,
  // overhanging leaves), walking past at their own depths; it dissolves into her painted pond after the stop and then leaves the GPU
  layers: [
    { src: "/arcs/pond/walk/sky.webp", wrap: 3.48, th: 0.45, h: 8.4, y: 1.8, r: 4.88, order: 0, walk: 0.05, glint: [0.5, 0.5, 0.8], mist: 0.06, mistCol: "#3a2c4a", show: [-1, 0, 7.9, 10.2], release: 10.6 },
    { src: "/arcs/pond/walk/far.webp", lazy: true, wrap: 3.48, th: 0.3, h: 8.4, y: 1.8, r: 4.86, order: 1, walk: 0.14, show: [-1, 0, 7.9, 10.2], release: 10.6 },
    { src: "/arcs/pond/walk/near.webp", lazy: true, wrap: 3.48, th: 0.5, h: 8.4, y: 1.8, r: 4.84, order: 2, walk: 0.3, show: [-1, 0, 7.9, 10.2], release: 10.6 },
    { src: "/arcs/pond/walk/drift.webp", lazy: true, wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.82, order: 3, add: true, flow: [0.011, 0.024], opacity: 0.42, show: [-1, 0.6, 7.2, 9.4], release: 10.6 },
    { src: "/arcs/pond/walk/leaves.webp", lazy: true, wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.8, order: 4, walk: 0.5, sway: 0.05, show: [-1, 0, 7.8, 9.8], release: 10.6 },
    { src: "/arcs/pond/pond.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, order: 9, water: 0.4, mist: 0.1, mistCol: "#2e2440", opacity: [[0, 0.78], [8.5, 0.66], [16, 0.72], [17.5, 0.9]], show: [7.6, 10, 99, 99] },
  ],
  bob: [0.035, 0.9],
  steps: { src: "/sounds/pond/feet-in-leaves.mp3", vol: 0.32, fade: [4.9, 6.15] },
  sounds: ["/sounds/pond/feet-in-leaves.mp3"],
  covered: 7.9,
  // the head turns toward a glimpse, the eyes blink in disbelief, the footsteps stop; then a slow approach and a long look
  lids: [6.2, 6.7],
  ambient: { scene: "duskwalk", until: 99, then: [[7.4, "reveal"], [14.5, "pond"]] },
  look: { start: 0.08, lift: [14, 17], end: -0.12, yaw: [[0, -0.12], [4.8, -0.08], [5.7, 0.55], [7.6, 0.5], [9.5, 0], [28, 0]] },
  dolly: [[0, 0], [9, 0], [15, 0.35], [21, 0.8], [28, 0.9]],
  fov: [[0, 72], [15, 71], [21, 63], [28, 62]],
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "pond", pos: "low" },
  speed: [[0, 1], [7.2, 1], [8, 0], [28, 0]],
  frag: `
uniform float uT, uPhase, uRadius, uSurface;
uniform vec4 uPart[24];
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
vec3 sunset(float y){
  vec3 low = vec3(0.96, 0.5, 0.26), mid = vec3(0.62, 0.24, 0.42), high = vec3(0.13, 0.11, 0.3);
  return mix(mix(low, mid, smoothstep(1.3, 3.0, y)), high, smoothstep(3.0, 7.5, y));
}
// a water lily seen from p: eight painted petals that open, a warm heart
vec4 lily(vec2 p, float open){
  float a = atan(p.y, p.x);
  float d = length(p);
  float pr = 0.26 * (0.25 + 0.75 * open) * (0.5 + 0.5 * pow(abs(cos(a * 4.0)), 0.55));
  float k = 1.0 - smoothstep(pr * 0.82, pr, d);
  vec3 c = mix(vec3(1.0, 0.95, 0.97), vec3(0.95, 0.6, 0.76), smoothstep(0.0, pr, d));
  c = mix(c, vec3(1.0, 0.82, 0.32), 1.0 - smoothstep(0.02, 0.05, d));
  return vec4(c, k * step(0.001, open));
}
vec3 pads(vec3 col, vec2 p, float open){
  float d = length(p);
  float a = atan(p.y, p.x);
  float pad = (1.0 - smoothstep(0.4, 0.44, d)) * step(0.18, abs(a - 0.6));
  col = mix(col, mix(vec3(0.12, 0.24, 0.16), vec3(0.2, 0.34, 0.2), smoothstep(0.0, 0.44, d)), pad * 0.92);
  vec4 l = lily(p, open);
  return mix(col, l.rgb, l.a);
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float y = vPos.y;
  float pond = step(8.5, uT);
  float curtain = smoothstep(6.5, 8.5, uT);
  vec3 col;
  if (uSurface < 0.5) {
    // under the painted walk: only a soft dusk, no drawn trunks
    vec3 walk = mix(vec3(0.42, 0.3, 0.42), vec3(0.14, 0.12, 0.24), smoothstep(1.0, 8.0, y));
    // the hidden pond at sunset: sky over a still horizon, framing trees, water with lilies
    float hz = 1.25 + 0.03 * sin(th * 3.0);
    vec3 sky = sunset(y) + 0.03 * n;
    float sun = exp(-pow(th * 2.2, 2.0) - pow((y - 1.5) * 2.4, 2.0));
    sky += vec3(1.0, 0.7, 0.35) * sun * 0.6;
    vec3 water = sunset(2.0 * hz - y) * 0.55 + vec3(0.03, 0.04, 0.09);
    water += vec3(1.0, 0.6, 0.3) * exp(-pow(th * 3.0, 2.0)) * 0.25 * (0.6 + 0.4 * sin(y * 40.0 + uT * 1.5 + n * 6.0));
    vec3 scene = y > hz ? sky : water;
    float frame = smoothstep(1.15, 1.6, abs(th)) * step(0.5, hash(floor((th * uRadius) / 0.5) + 9.0));
    scene = mix(scene, vec3(0.08, 0.07, 0.1), frame * smoothstep(hz - 0.2, hz + 0.3, y));
    if (y < hz) {
      ${"__WALL_LILIES__"}
    }
    col = mix(walk, scene, pond);
    // the leaves: dense foliage closing in front, parted by touch, then fully open
    float lf = snoise(vec3(th * uRadius * 1.4, y * 1.5, 1.0)) * 0.5 + 0.5;
    float lf2 = snoise(vec3(th * uRadius * 4.0, y * 4.5, 3.0)) * 0.5 + 0.5;
    vec3 leaf = mix(vec3(0.05, 0.12, 0.08), vec3(0.16, 0.3, 0.14), lf2) + vec3(0.45, 0.25, 0.08) * smoothstep(0.75, 0.95, lf2) * 0.35;
    float open = 0.0;
    for (int i = 0; i < 24; i++){
      vec4 m = uPart[i];
      if (m.w <= 0.0) continue;
      float r = 1.35 * smoothstep(0.0, 1.1, uT - m.w);
      open = max(open, 1.0 - smoothstep(r * 0.65, r, distance(vPos, m.xyz) * (0.85 + 0.35 * lf)));
    }
    float auto = (uT - 14.2) * 2.6;
    open = max(open, 1.0 - smoothstep(auto - 1.0, auto, length(vec2(th * uRadius, y - 1.5)) + lf * 0.8));
    float cover = curtain * (1.0 - open);
    col = mix(col, leaf, cover * smoothstep(0.2, 0.45, lf + 0.25));
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    vec3 earth = mix(vec3(0.12, 0.09, 0.08), vec3(0.2, 0.15, 0.11), n);
    float reach = (uT - 14.5) * 2.2;
    float wet = 1.0 - smoothstep(reach - 1.2, reach, length(xz - vec2(0.0, -3.5)) + n * 0.8);
    vec3 water = mix(vec3(0.3, 0.16, 0.28), vec3(0.1, 0.08, 0.2), smoothstep(0.0, 5.0, length(xz))) + vec3(0.9, 0.5, 0.25) * exp(-pow(atan(xz.x, -xz.y) * 2.5, 2.0)) * 0.22;
    water += 0.04 * sin(length(xz) * 14.0 - uT * 1.2) * smoothstep(1.0, 4.0, length(xz));
    ${"__FLOOR_LILIES__"}
    col = mix(earth, water, wet);
  } else {
    float gaps = smoothstep(0.55, 0.7, snoise(vec3(vPos.xz * 0.9, 2.0)) * 0.5 + 0.5);
    vec3 canopy = mix(vec3(0.04, 0.07, 0.06), vec3(0.32, 0.2, 0.32), gaps);
    col = mix(canopy, vec3(0.12, 0.1, 0.26) + 0.03 * n, smoothstep(14.5, 18.0, uT));
  }
  col = mix(col, col * vec3(0.78, 0.8, 0.95), smoothstep(23.0, 28.0, uT) * 0.5);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`
    .replace("__WALL_LILIES__", LILIES_WALL.map(([a, b], i) => `scene = pads(scene, vec2((th - (${a.toFixed(2)})) * uRadius, (y - ${b.toFixed(2)}) * 2.6), smoothstep(${(17.5 + i * 0.7).toFixed(2)}, ${(19 + i * 0.7).toFixed(2)}, uT));`).join("\n"))
    .replace("__FLOOR_LILIES__", LILIES_FLOOR.map(([a, b], i) => `water = pads(water, (xz - vec2(${a.toFixed(2)}, ${b.toFixed(2)})) * 1.1, smoothstep(${(17 + i * 0.6).toFixed(2)}, ${(18.6 + i * 0.6).toFixed(2)}, uT));`).join("\n")),
};
